import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AppExtraLoadingService } from './app-extra-loading.service';

type ToastPreset = 'error' | 'warning' | 'success' | 'info' | 'custom';
type ToastColor = 'primary' | 'dark' | 'black' | 'gray' | 'green' | 'warn' | 'orange' | 'green';

interface Toast {
  size: 'tiny' | 'small' | 'medium' | 'large';
  title: string;
  message: string;
  icon: string;
  image: string;
  customSvg?: string;
  imageSize: number;
  backgroundImage: string;
  backgroundImageSize: number;
  color: ToastColor;
  duration: number;
  fontSize?: number;
  titleFontSize?: number;
  closable: boolean;
  vertical: boolean;
  cta?: {
    color: string;
    text?: string;
    icon?: string;
    height?: string;
    size?: string;
    maxWidth?: string;
    onClick: () => void;
  };
  onClose?: () => void;
  location: 'head' | 'footer' | 'bottom-left' | 'bottom-right';
}

const defaultToastOptions: Toast = {
  closable: false,
  duration: 2500,
  size: 'small',
  icon: undefined,
  image: undefined,
  imageSize: 64,
  backgroundImage: undefined,
  backgroundImageSize: 128,
  color: 'black',
  title: undefined,
  message: undefined,
  location: 'head',
  vertical: false,
};

@Injectable({
  providedIn: 'root',
})
export class ToastService {
  private appExtraLoadingService = inject(AppExtraLoadingService);
  private toasts: Toast[] = [];
  toasts$ = new BehaviorSubject<Toast[]>(this.toasts);
  presets = {
    error: { icon: '/assets/icons/cross-circle.svg', color: 'warn' },
    warning: { icon: '/assets/icons/alert-circle.svg', color: 'orange' },
    success: { icon: '/assets/icons/check-circle.svg', color: 'green' },
    info: { icon: '/assets/icons/info-circle.svg', color: 'primary' },
  };

  add(message: string, preset: ToastPreset, options: Partial<Toast> = {}) {
    this.appExtraLoadingService.load$.next('toasts');
    const defaultOptions = { ...defaultToastOptions };
    if (options.closable && !options.duration) defaultOptions.duration = undefined;
    if (preset && this.presets[preset]) {
      defaultOptions.icon = this.presets[preset].icon;
      defaultOptions.color = this.presets[preset].color;
    }
    const newToast: Toast = { ...defaultOptions, ...options, message };
    this.toasts.push(newToast);
    this.toasts$.next(this.toasts);
    if (newToast.duration !== undefined) {
      setTimeout(() => {
        const index = this.toasts.indexOf(newToast);
        if (index > -1) {
          this.toasts.splice(index, 1);
          this.toasts$.next(this.toasts);
        }
      }, newToast.duration);
    }
    return newToast;
  }

  public close(toast: Toast) {
    if (toast.onClose) toast.onClose();
    const index = this.toasts.indexOf(toast);
    if (index > -1) this.toasts.splice(index, 1);
    this.toasts$.next(this.toasts);
  }
}
